/* eslint-disable max-depth */
// @flow

// import { stringify } from 'uuid';

import { BrowserDetection } from '@jitsi/js-utils';
import axios from 'axios';
import { sha256 } from 'js-sha256';
import moment from 'moment';

import {
    CHROME,
    FIREFOX,
    INTERNET_EXPLORER,
    OPERA,
    SAFARI
} from '../../../../browser';
import {
    ACTION_PINNED,
    ACTION_UNPINNED,
    createOfferAnswerFailedEvent,
    createPinnedEvent,
    sendAnalytics
} from '../../analytics';
import { reloadNow } from '../../app/actions';
import { openDisplayNamePrompt } from '../../display-name';
import { showErrorNotification } from '../../notifications';
import { apiKEY } from '../config/constants';
import { CONNECTION_ESTABLISHED, CONNECTION_FAILED, connectionDisconnected } from '../connection';
import { SET_USER_SETTING } from '../jwt';
import { MHConferenceErrors } from '../lib-meet-hour';
import { MEDIA_TYPE } from '../media';
import { _autoLiveStreaming } from '../mtApi';
import {
    PARTICIPANT_ROLE,
    PARTICIPANT_UPDATED,
    PIN_PARTICIPANT,
    getLocalParticipant,
    getParticipantById,
    getPinnedParticipant
} from '../participants';
import { MiddlewareRegistry, StateListenerRegistry } from '../redux';
import { getModeratorOptions } from '../sounds/functions.any';
import { TRACK_ADDED, TRACK_REMOVED } from '../tracks';
import { checkIfReactNative, isDomainWeborNative } from '../util/checkOS';

import {
    CONFERENCE_FAILED,
    CONFERENCE_JOINED,
    CONFERENCE_SUBJECT_CHANGED,
    CONFERENCE_WILL_LEAVE,
    FORCE_MUTE_REMOTE_PARTICIPANT,
    FORCE_MUTE_REMOTE_PARTICIPANT_VIDEO,
    MODERATOR_MUTED_US_ALL,
    SEND_TONES,
    SET_PENDING_SUBJECT_CHANGE,
    SET_ROOM
} from './actionTypes';
import {
    conferenceFailed,
    conferenceWillLeave,
    createConference,
    moderatorForceMuted,
    setSubject,
    setTimeOfUser
} from './actions';
import {
    _addLocalTracksToConference,
    _removeLocalTracksFromConference,
    forEachConference,
    getCurrentConference
} from './functions';
import logger from './logger';


declare var APP: Object;

/**
 * Maps the names of the browsers from bowser to the internal names defined in
 * ./browsers.js.
 */
const bowserNameToJitsiName = {
    'Chrome': CHROME,
    'Chromium': CHROME,
    'Opera': OPERA,
    'Firefox': FIREFOX,
    'Internet Explorer': INTERNET_EXPLORER,
    'Safari': SAFARI
};

/**
 * Handler for before unload event.
 */
let beforeUnloadHandler;

const FORE_MUTE_SENT = 'FORCE_MUTE_SENT';

/**
 * Implements the middleware of the feature base/conference.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    const { getState } = store;
    const state = getState();
    const conference = getCurrentConference(state);

    switch (action.type) {
    case CONFERENCE_FAILED:
        return _conferenceFailed(store, next, action);

    case CONFERENCE_JOINED:
        return _conferenceJoined(store, next, action);

    case CONNECTION_ESTABLISHED:
        return _connectionEstablished(store, next, action);

    case CONNECTION_FAILED:
        return _connectionFailed(store, next, action);

    case CONFERENCE_SUBJECT_CHANGED:
        return _conferenceSubjectChanged(store, next, action);

    case CONFERENCE_WILL_LEAVE:
        _conferenceWillLeave();
        break;

    case FORCE_MUTE_REMOTE_PARTICIPANT: {

        if (conference) {
            conference.muteParticipant(action.id, action.mediaType);
            _forceMuteSent(action, conference, state);
        }
        break;
    }

    case FORCE_MUTE_REMOTE_PARTICIPANT_VIDEO: {
        if (conference) {
            conference.muteParticipant(action.id, action.mediaType);
        }
        break;
    }

    case MODERATOR_MUTED_US_ALL:
        return _moderatedMute(action, conference, state);

    case PARTICIPANT_UPDATED:
        return _updateLocalParticipantInConference(store, next, action);

    case PIN_PARTICIPANT:
        return _pinParticipant(store, next, action);

    case SEND_TONES:
        return _sendTones(store, next, action);

    case SET_ROOM:
        return _setRoom(store, next, action);

    case TRACK_ADDED:
    case TRACK_REMOVED:
        return _trackAddedOrRemoved(store, next, action);
    }

    return next(action);
});

/**
 * Makes sure the Other moderator knows if you force muted or not.
 *
 * @param {Object} action - The redux action {@code FORE_MUTE_SENT} which is
 * being dispatched in the specified {@code store}.
 * @param {Object} conference - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 *  @param {Object} state - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _forceMuteSent(action, conference, state) {
    const localId = getLocalParticipant(state).id;
    const { lockAudio, lockVideo } = state['features/base/conference'];

    if (action.mediaType === 'audio') {
        lockAudio[`${action.id}`] = action.bool;
        conference.sendCommand(FORE_MUTE_SENT, {
            attributes: {
                mediaType: action.mediaType,
                remoteAudios: JSON.stringify(lockAudio),
                sentID: localId


            }
        });
    } else {
        lockVideo[`${action.id}`] = action.bool;
        conference.sendCommand(FORE_MUTE_SENT, {
            attributes: {
                mediaType: action.mediaType,
                remoteAudios: JSON.stringify(lockVideo),
                sentID: localId


            }
        });
    }

}

/**
 * Makes sure the Other moderator knows if you force muted or not.
 *
 * @param {Object} action - The redux action {@code FORE_MUTE_SENT} which is
 * being dispatched in the specified {@code store}.
 * @param {Object} conference - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Object} state - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _moderatedMute(action, conference, state) {
    const localId = getLocalParticipant(state).id;
    const { lockAudio, lockVideo } = state['features/base/conference'];

    if (action?.mediaType === 'audio') {
        if (conference) {
            conference.sendCommand(FORE_MUTE_SENT, {
                attributes: {
                    mediaType: action.mediaType,
                    remoteAudios: JSON.stringify(lockAudio),
                    sentID: localId


                }
            });
        }
    } else if (conference) {
        conference.sendCommand(FORE_MUTE_SENT, {
            attributes: {
                mediaType: action.mediaType,
                remoteAudios: JSON.stringify(lockVideo),
                sentID: localId


            }
        });
    }
}

/**
 * Set up state change listener to perform maintenance tasks when the conference
 * is left or failed, e.g.
 * Open.
 */
StateListenerRegistry.register(
    state => getCurrentConference(state),
    (conference, store, previousConference) => {
        if (conference && conference !== previousConference) {
            conference.addCommandListener(FORE_MUTE_SENT,
                ({ attributes }) => {
                    const { dispatch } = store;

                    if (attributes) {
                        const { mediaType, sentID, remoteAudios } = attributes;

                        if (sentID) {

                            dispatch(moderatorForceMuted(mediaType, JSON.parse(remoteAudios)));
                        }

                    }

                }
            );

        }
    });

/**
 * Makes sure to leave a failed conference in order to release any allocated
 * resources like peer connections, emit participant left events, etc.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code CONFERENCE_FAILED} which is
 * being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _conferenceFailed({ dispatch, getState }, next, action) {
    const result = next(action);
    const { conference, error } = action;
    const { enableForcedReload } = getState()['features/base/config'];

    // Handle specific failure reasons.
    switch (error.name) {
    case MHConferenceErrors.CONFERENCE_DESTROYED: {
        const [ reason ] = error.params;

        dispatch(showErrorNotification({
            description: reason,
            titleKey: 'dialog.sessTerminated'
        }));

        break;
    }
    case MHConferenceErrors.CONFERENCE_RESTARTED: {
        if (enableForcedReload) {
            dispatch(showErrorNotification({
                description: 'dialog.restartInitiatedBecauseOfBridgeFailure',
                titleKey: 'dialog.sessionRestarted'
            }));
        }

        break;
    }
    case MHConferenceErrors.CONNECTION_ERROR: {
        const [ msg ] = error.params;

        dispatch(connectionDisconnected(getState()['features/base/connection'].connection));
        dispatch(showErrorNotification({
            descriptionArguments: { msg },
            descriptionKey: msg ? 'dialog.connectErrorWithMsg' : 'dialog.connectError',
            titleKey: 'connection.CONNFAIL'
        }));

        break;
    }
    case MHConferenceErrors.OFFER_ANSWER_FAILED:
        sendAnalytics(createOfferAnswerFailedEvent());
        break;
    }

    if (typeof APP === 'undefined') {
        !error.recoverable
        && conference
        && conference.leave().catch(reason => {
            // Even though we don't care too much about the failure, it may be
            // good to know that it happen, so log it (on the info level).
            logger.info('MHConference.leave() rejected with:', reason);
        });
    } else if (typeof beforeUnloadHandler !== 'undefined') {
        // FIXME: Workaround for the web version. Currently, the creation of the
        // conference is handled by /conference.js and appropriate failure handlers
        // are set there.
        window.removeEventListener('unload', beforeUnloadHandler);
        beforeUnloadHandler = undefined;
    }

    if (enableForcedReload && error?.name === MHConferenceErrors.CONFERENCE_RESTARTED) {
        dispatch(conferenceWillLeave(conference));
        dispatch(reloadNow());
    }

    return result;
}

/**
 * Does extra sync up on properties that may need to be updated after the
 * conference was joined.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code CONFERENCE_JOINED} which is
 * being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _conferenceJoined({ dispatch, getState }, next, action) {
    const result = next(action);
    const { conference } = action;
    const { pendingSubjectChange } = getState()['features/base/conference'];
    const { requireDisplayName } = getState()['features/base/config'];

    conference.addCommandListener(FORE_MUTE_SENT,
        ({ attributes }) => {
            const state = getState();

            const localParticipant = getLocalParticipant(state);

            if (attributes && localParticipant?.role === PARTICIPANT_ROLE.MODERATOR) {
                const { mediaType, sentID, remoteAudios } = attributes;

                if (sentID !== localParticipant.id) {

                    dispatch(moderatorForceMuted(mediaType, JSON.parse(remoteAudios)));
                }

            }

        }
    );

    pendingSubjectChange && dispatch(setSubject(pendingSubjectChange));

    // FIXME: Very dirty solution. This will work on web only.
    // When the user closes the window or quits the browser, lib-meet-hour
    // handles the process of leaving the conference. This is temporary solution
    // that should cover the described use case as part of the effort to
    // implement the conferenceWillLeave action for web.
    beforeUnloadHandler = () => {
        dispatch(conferenceWillLeave(conference));
    };
    window.addEventListener('unload', beforeUnloadHandler);

    if (requireDisplayName
        && !getLocalParticipant(getState)?.name
        && !getState()['features/base/settings']?.displayName
        && !conference.isHidden()) {
        dispatch(openDisplayNamePrompt(undefined));
    }

    _sendUserAttendance({ dispatch,
        getState }, action);

    _autoLiveStreaming({
        dispatch,
        getState
    });


    return result;
}

/**
 * Notifies the feature base/conference that the action
 * {@code CONNECTION_ESTABLISHED} is being dispatched within a specific redux
 * store.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code CONNECTION_ESTABLISHED}
 * which is being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _connectionEstablished({ dispatch }, next, action) {
    const result = next(action);

    // FIXME: Workaround for the web version. Currently, the creation of the
    // conference is handled by /conference.js.
    typeof APP === 'undefined' && dispatch(createConference());

    return result;
}

/**
 * Notifies the feature base/conference that the action
 * {@code CONNECTION_FAILED} is being dispatched within a specific redux
 * store.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code CONNECTION_FAILED} which is
 * being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _connectionFailed({ dispatch, getState }, next, action) {
    const result = next(action);

    if (typeof beforeUnloadHandler !== 'undefined') {
        window.removeEventListener('unload', beforeUnloadHandler);
        beforeUnloadHandler = undefined;
    }

    // FIXME: Workaround for the web version. Currently, the creation of the
    // conference is handled by /conference.js and appropriate failure handlers
    // are set there.
    if (typeof APP === 'undefined') {
        const { connection } = action;
        const { error } = action;

        forEachConference(getState, conference => {
            // It feels that it would make things easier if MHConference
            // in lib-meet-hour would monitor it's connection and emit
            // CONFERENCE_FAILED when it's dropped. It has more knowledge on
            // whether it can recover or not. But because the reload screen
            // and the retry logic is implemented in the app maybe it can be
            // left this way for now.
            if (conference.getConnection() === connection) {
                // XXX Note that on mobile the error type passed to
                // connectionFailed is always an object with .name property.
                // This fact needs to be checked prior to enabling this logic on
                // web.
                const conferenceAction
                    = conferenceFailed(conference, error.name);

                // Copy the recoverable flag if set on the CONNECTION_FAILED
                // action to not emit recoverable action caused by
                // a non-recoverable one.
                if (typeof error.recoverable !== 'undefined') {
                    conferenceAction.error.recoverable = error.recoverable;
                }

                dispatch(conferenceAction);
            }

            return true;
        });
    }

    return result;
}

/**
 * Notifies the feature base/conference that the action
 * {@code CONFERENCE_SUBJECT_CHANGED} is being dispatched within a specific
 *  redux store.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code CONFERENCE_SUBJECT_CHANGED}
 * which is being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _conferenceSubjectChanged({ dispatch, getState }, next, action) {
    const result = next(action);
    const { subject } = getState()['features/base/conference'];

    if (subject) {
        dispatch({
            type: SET_PENDING_SUBJECT_CHANGE,
            subject: undefined
        });
    }

    typeof APP === 'object' && APP.API.notifySubjectChanged(subject);

    return result;
}

/**
 * Notifies the feature base/conference that the action
 * {@code CONFERENCE_WILL_LEAVE} is being dispatched within a specific redux
 * store.
 *
 * @private
 * @returns {void}
 */
function _conferenceWillLeave() {
    if (typeof beforeUnloadHandler !== 'undefined') {
        window.removeEventListener('unload', beforeUnloadHandler);
        beforeUnloadHandler = undefined;
    }
}

/**
 * Notifies the feature base/conference that the action {@code PIN_PARTICIPANT}
 * is being dispatched within a specific redux store. Pins the specified remote
 * participant in the associated conference, ignores the local participant.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code PIN_PARTICIPANT} which is
 * being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _pinParticipant({ getState }, next, action) {
    const state = getState();
    const { conference } = state['features/base/conference'];

    if (!conference) {
        return next(action);
    }

    const participants = state['features/base/participants'];
    const id = action.participant.id;
    const participantById = getParticipantById(participants, id);
    const pinnedParticipant = getPinnedParticipant(participants);
    const actionName = id ? ACTION_PINNED : ACTION_UNPINNED;
    const local
        = (participantById && participantById.local)
            || (!id && pinnedParticipant && pinnedParticipant.local);
    let participantIdForEvent;

    if (local) {
        participantIdForEvent = local;
    } else {
        participantIdForEvent
            = actionName === ACTION_PINNED ? id : pinnedParticipant && pinnedParticipant.id;
    }

    sendAnalytics(createPinnedEvent(
        actionName,
        participantIdForEvent,
        {
            local,
            'participant_count': conference.getParticipantCount()
        }));

    return next(action);
}

/**
 * Requests the specified tones to be played.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code SEND_TONES} which is
 * being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _sendTones({ getState }, next, action) {
    const state = getState();
    const { conference } = state['features/base/conference'];

    if (conference) {
        const { duration, tones, pause } = action;

        conference.sendTones(tones, duration, pause);
    }

    return next(action);
}

/**
 * Notifies the feature base/conference that the action
 * {@code SET_ROOM} is being dispatched within a specific
 *  redux store.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code SET_ROOM}
 * which is being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _setRoom({ dispatch, getState }, next, action) {
    const state = getState();
    const { subject } = state['features/base/config'];
    const { room } = action;

    if (room) {
        // Set the stored subject.
        dispatch(setSubject(subject));
    }

    return next(action);
}

/**
 * Synchronizes local tracks from state with local tracks in MHConference
 * instance.
 *
 * @param {Store} store - The redux store.
 * @param {Object} action - Action object.
 * @private
 * @returns {Promise}
 */
function _syncConferenceLocalTracksWithState({ getState }, action) {
    const conference = getCurrentConference(getState);
    let promise;

    if (conference) {
        const track = action.track.mhTrack;

        if (action.type === TRACK_ADDED) {
            promise = _addLocalTracksToConference(conference, [ track ]);
        } else {
            promise = _removeLocalTracksFromConference(conference, [ track ]);
        }
    }

    return promise || Promise.resolve();
}

/**
 * Notifies the feature base/conference that the action {@code TRACK_ADDED}
 * or {@code TRACK_REMOVED} is being dispatched within a specific redux store.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code TRACK_ADDED} or
 * {@code TRACK_REMOVED} which is being dispatched in the specified
 * {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _trackAddedOrRemoved(store, next, action) {
    const track = action.track;

    // TODO All track swapping should happen here instead of conference.js.
    // Since we swap the tracks for the web client in conference.js, ignore
    // presenter tracks here and do not add/remove them to/from the conference.
    if (track && track.local && track.mediaType !== MEDIA_TYPE.PRESENTER) {
        return (
            _syncConferenceLocalTracksWithState(store, action)
                .then(() => next(action)));
    }

    return next(action);
}

/**
 * Updates the conference object when the local participant is updated.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action which is being dispatched in the
 * specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _updateLocalParticipantInConference({ dispatch, getState }, next, action) {
    const { conference } = getState()['features/base/conference'];
    const { participant } = action;
    const result = next(action);

    const localParticipant = getLocalParticipant(getState);

    if (conference && participant?.id === localParticipant?.id) {
        if ('name' in participant) {
            conference.setDisplayName(participant.name);
        }

        if ('role' in participant && participant.role === PARTICIPANT_ROLE.MODERATOR) {
            const { pendingSubjectChange, subject } = getState()['features/base/conference'];

            // When the local user role is updated to moderator and we have a pending subject change
            // which was not reflected we need to set it (the first time we tried was before becoming moderator).
            if (typeof pendingSubjectChange !== 'undefined' && pendingSubjectChange !== subject) {
                dispatch(setSubject(pendingSubjectChange));
            }
        }
    }

    return result;
}


/**
 *  User Attendance when it joins the conference.
 *
 * @param {any} {getState} - Redux functions.
 * @param {any} action - Redux actions.
 * @returns {any}
 */
async function _sendUserAttendance({ dispatch, getState, isLogout = false }) {
    const state = getState();
    const { API_URL_PREFIX, API_VERSION_PREFIX, MH_CLIENT_ID } = state['features/base/config'];
    const localPart = getLocalParticipant(state)?.name?.toLowerCase();

    if (localPart === 'youtube') {
        return;
    }

    // console.log('frtgbhhh', state['features/base/conference']?.conference?.room?.meetingId);

    try {
        const format1 = 'YYYY-MM-DD HH:mm:ss';
        const currentDateTime = moment().utc()
        .format(format1);
        const bowser = new BrowserDetection();
        const browserName = bowser.getName();
        let accessType = 'web';
        const participant = getLocalParticipant(state);

        for (const i in bowserNameToJitsiName) {
            if (i !== browserName) {
                accessType = 'app';
            }
        }
        const roomName = getState()['features/base/conference'].room;

        if (!checkIfReactNative()) {

            let dataJson;

            try {
                const ClientIP = await axios({
                    url: 'https://api.ipify.org/?format=json',
                    timeout: 8000,
                    method: 'get',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                // eslint-disable-next-line no-negated-condition, max-depth
                if (typeof ClientIP !== 'undefined') {

                    const ClientIPData = await ClientIP?.data;

                    const dataGeo = `${API_URL_PREFIX}${API_VERSION_PREFIX}geolocation?api_key=${apiKEY}&ip_address=${ClientIPData?.ip}`;
                    const getData = await axios({
                        url: dataGeo,
                        timeout: 8000,
                        method: 'get',
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    });

                    // eslint-disable-next-line max-depth
                    if (getData) {
                        dataJson = await getData?.data;
                    }
                }
            } catch (error) {
                logger.error('Error in fetching geolocation bros', error);

            }


            const { occurenceId } = state['features/base/mtApi'];

            const moement = moment().seconds(0)
        .milliseconds(0)
        .format('X');

            const mesh256 = sha256(`${apiKEY}:MH:${moement}`);


            let userObject = {};

            if (isLogout === true) {
                userObject = {
                    logout_datetime: currentDateTime

                };
            } else {
                userObject = {
                    login_datetime: currentDateTime

                };
            }

            userObject = {
                ...userObject,
                client_id: MH_CLIENT_ID,
                credentials: mesh256,
                ...isDomainWeborNative,
                meeting_id: roomName,
                name: participant?.name,
                occurrence_id: occurenceId,
                is_host: state['features/base/jwt'].jwt ? 1 : 0,
                email_id: participant?.email,
                login_datetime: currentDateTime,
                user_agent: browserName,
                access_type: accessType,
                ip: dataJson?.IPv4,
                geolocation: dataJson
            };

            const userJson = JSON.stringify(userObject);
            const prejoinAP = `${API_URL_PREFIX}${API_VERSION_PREFIX}meeting/save_meeting_attendance`;
            // eslint-disable-next-line no-unused-vars
            const prejoinResponse = await axios({
                url: prejoinAP,
                timeout: 8000,
                method: 'post',
                headers: { 'Content-Type': 'application/json' },
                data: userJson
            }).catch(es => logger.error('Error in fetching prejoin api For guest', es));

        }

        const userDate = new Date();

        dispatch(setTimeOfUser(userDate));

        const { jwt } = state['features/base/jwt'];

        if (!jwt) {

            const settings = await getModeratorOptions(state);

            if (settings) {

                dispatch({ type: SET_USER_SETTING,
                    confSetting: settings });
            }

        }

    // await sendUserData.json();
    } catch (e) {
        logger.error('Error in fetching geolocation');
    }
}

